import {
  commandValues,
  interfaceConfigTypeValues,
  countryCode,
  dhcpDnsValues,
  addressModeValues,
  securityValues,
  interfaceConfigTypes,
  wifiDevicesToScreen,
} from './values';

const disableInterfaceConfigurationType = (
  values: { [key: string]: string },
  extraRowFlag: boolean,
) => !(values.command && values.command === commandValues[0].value && !extraRowFlag);
const disabledIfDeleteWifi = (values: { [key: string]: string }) =>
  !(values.command && values.command === commandValues[0].value);
const disabledIfAccessPoint = (values: { [key: string]: string }) =>
  !(values.ACCESS_POINT && values.ACCESS_POINT === interfaceConfigTypeValues[1].value);
const disabledIfClientWithDhcp = (values: { [key: string]: string }) =>
  !(values.CLIENT_WITH_DHCP && values.CLIENT_WITH_DHCP === interfaceConfigTypeValues[0].value);

export interface InputsWithValidators {
  label: string;
  key: string;
  validator?: Function;
  helperText?: string;
  values?: { label: string; value: string }[];
  defaultValue?: { label: string; value: string };
  disabled?: any;
  required: boolean;
}
export const wifiConfigInputData: InputsWithValidators[] = [
  {
    label: 'Command',
    key: 'command',
    values: commandValues,
    defaultValue: commandValues[0],
    disabled: false,
    required: true,
  },
  {
    label: 'Country Code',
    key: 'countryCode',
    values: countryCode,
    defaultValue: countryCode[0],
    disabled: disabledIfDeleteWifi,
    required: true,
  },
  {
    label: 'Channel',
    key: 'channel',
    validator: (value: string) => Number.parseInt(value, 10) <= 165,
    helperText: 'Must be a number less than 165',
    defaultValue: { label: '11', value: '11' },
    disabled: disabledIfDeleteWifi,
    required: true,
  },
  {
    label: 'Password',
    key: 'password',
    disabled: disabledIfDeleteWifi,
    validator: (value: string) => value.length >= 8,
    helperText: '8 Character Minimum',
    required: true,
  },
  {
    label: 'Interface Config Type',
    key: 'interfaceConfigType',
    values: interfaceConfigTypeValues,
    defaultValue: interfaceConfigTypeValues[0],
    disabled: disableInterfaceConfigurationType,
    required: true,
  },
  {
    label: 'Security',
    key: 'security',
    values: securityValues,
    defaultValue: securityValues[0],
    disabled: disabledIfDeleteWifi,
    required: true,
  },
  {
    label: 'Metric',
    key: 'metric',
    defaultValue: { label: '30', value: '30' },
    disabled: disabledIfDeleteWifi,
    required: true,
    helperText: 'Integer between 0 and 30',
    validator: (value: string) => {
      const parsedNumber = Number.parseInt(value, 10);
      return value.length === `${parsedNumber}`.length && parsedNumber <= 30 && parsedNumber >= 0;
    },
  },
  {
    label: 'Transaction ID',
    key: 'transactionId',
    disabled: (values: { [key: string]: string }) => !values.command,
    required: false,
    validator: (value: string) => value && value.length === 36,
    helperText: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
  },
  {
    label: 'SSID',
    key: 'ssid',
    disabled: disabledIfDeleteWifi,
    required: true,
  },
  {
    label: 'DHCP DNS Config',
    key: 'dhcpDnsConfig',
    values: dhcpDnsValues,
    defaultValue: dhcpDnsValues[0],
    disabled: (values: { [key: string]: string }) =>
      disabledIfDeleteWifi(values) && disabledIfAccessPoint(values),
    required: true,
  },

  {
    label: 'Address Mode',
    key: 'addressMode',
    values: addressModeValues,
    defaultValue: addressModeValues[0],
    disabled: (values: { [key: string]: string }) =>
      disabledIfDeleteWifi(values) && disabledIfAccessPoint(values),
    required: true,
  },
  {
    label: 'IP Address',
    key: 'ipAddress',
    validator: (value: string) =>
      value &&
      /^(25[0-5]|2[0-4]\d|[01]?\d{1,2})\.(25[0-5]|2[0-4]\d|[01]?\d{1,2})\.(25[0-5]|2[0-4]\d|[01]?\d{1,2})\.(25[0-5]|2[0-4]\d|[01]?\d{1,2})$/.test(
        value,
      ),
    helperText: 'XXX.XXX.XX.XXX',
    disabled: (values: { [key: string]: string }) =>
      disabledIfDeleteWifi(values) && disabledIfClientWithDhcp(values),
    required: false,
  },
  {
    label: 'Network Mask',
    key: 'networkMask',
    validator: (value: string) =>
      value &&
      /^(25[0-5]|2[0-4]\d|[01]?\d{1,2})\.(25[0-5]|2[0-4]\d|[01]?\d{1,2})\.(25[0-5]|2[0-4]\d|[01]?\d{1,2})\.(25[0-5]|2[0-4]\d|[01]?\d{1,2})$/.test(
        value,
      ),
    helperText: 'XXX.XXX.XX.XXX',
    disabled: (values: { [key: string]: string }) =>
      disabledIfDeleteWifi(values) && disabledIfClientWithDhcp(values),
    required: false,
  },
];
export const wifiConfigInputs = (deviceType: string | null) => {
  const wifiConfigInputsClone: InputsWithValidators[] = JSON.parse(
    JSON.stringify(wifiConfigInputData),
  );
  if (deviceType && wifiDevicesToScreen(deviceType))
    wifiConfigInputsClone[4].values = interfaceConfigTypes(deviceType);
  return wifiConfigInputsClone;
};

export const wifiConfigInputDataSecondRow: InputsWithValidators[] = [];
wifiConfigInputData.forEach((node) => {
  if (!['command', 'countryCode', 'channel'].includes(node.key)) {
    wifiConfigInputDataSecondRow.push({
      key: `${node.key}2`,
      label: node.label,
      validator: node.validator,
      helperText: node.helperText,
      disabled: node.disabled,
      required: node.required,
      values: node.values,
      defaultValue: node.defaultValue,
    });
  }
});

export const validateInputsAddConfiguration = (wifiInputs: { [key: string]: string }) =>
  !wifiConfigInputData.every((wifiInputDatum) => {
    if (wifiInputDatum.validator) {
      return wifiInputDatum.validator(wifiInputs[wifiInputDatum.key]);
    }
    return wifiInputs[wifiInputDatum.key];
  });

export const validateInputsSendConfiguration = (
  wifiInputs: { [key: string]: string },
  extraRowFlag: boolean,
) => {
  let usedFields = wifiConfigInputData;
  if (extraRowFlag) {
    usedFields = usedFields.concat(wifiConfigInputDataSecondRow);
  }
  const isFormValid = usedFields.every((wifiInputDatum) => {
    if (wifiInputs[wifiInputDatum.key] && wifiInputDatum.required && wifiInputDatum.validator) {
      return wifiInputDatum.validator(wifiInputs[wifiInputDatum.key]);
    }
    if (wifiInputDatum.required && !wifiInputDatum.validator) return wifiInputs[wifiInputDatum.key];
    return !wifiInputDatum.required;
  });

  return !isFormValid;
};
